<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '$lib/api/client';
  import type { User, Page, Card } from '$lib/api/types';

  export let params: { username?: string } = {};

  let user: User | null = null;
  let page: Page | null = null;
  let cards: Card[] = [];
  let loading = true;
  let error: string | null = null;

  // OGP image dimensions (1200x630)
  const OGP_WIDTH = 1200;
  const OGP_HEIGHT = 630;

  // Layout configuration - optimized for OGP
  const SIDEBAR_WIDTH = 320;
  const PADDING = 28;

  // Grid configuration - scaled down to show more cards
  const GRID_COLS = 8;
  const GAP = 4;
  const CELL_WIDTH = 52; // Fixed small size
  const CELL_HEIGHT = CELL_WIDTH; // Square cells

  // Calculate actual grid dimensions
  const ACTUAL_GRID_WIDTH = CELL_WIDTH * 8 + GAP * 7;
  const GRID_AREA_WIDTH = OGP_WIDTH - SIDEBAR_WIDTH;
  const GRID_AREA_HEIGHT = OGP_HEIGHT - PADDING * 2;
  const GRID_LEFT_OFFSET = SIDEBAR_WIDTH + (GRID_AREA_WIDTH - ACTUAL_GRID_WIDTH) / 2;

  onMount(async () => {
    if (!params.username) {
      error = 'not_found';
      loading = false;
      return;
    }

    try {
      const [userData, pageData, cardsData] = await Promise.all([
        api.users.get(params.username),
        api.pages.get(params.username),
        api.cards.list(params.username),
      ]);

      user = userData;
      page = pageData;
      cards = cardsData.cards;
    } catch (err: any) {
      console.error('Failed to load page:', err);
      error = err.code === 'NOT_FOUND' ? 'not_found' : err.message;
    } finally {
      loading = false;
    }
  });

  // Filter out sidebar links and limit visible cards
  $: gridCards = cards
    .filter(c => !(c.type === 'link' && c.data?.showInSidebar))
    .filter(c => c.y < 12); // Show more rows since cells are smaller

  // Theme colors
  function getThemeBgColor(theme: string): string {
    switch (theme) {
      case 'dark': return '#1a1a1a';
      case 'cyber': return '#0a0a1a';
      case 'kawaii': return '#fef3f9';
      case 'vrchat': return '#1a1a2e';
      default: return '#f8f9fa';
    }
  }

  function getThemeTextColor(theme: string): string {
    switch (theme) {
      case 'dark':
      case 'cyber':
      case 'vrchat':
        return '#ffffff';
      default:
        return '#1a1a1a';
    }
  }

  function getCardBgColor(theme: string): string {
    switch (theme) {
      case 'dark': return '#2a2a2a';
      case 'cyber': return '#1a1a2e';
      case 'kawaii': return '#ffffff';
      case 'vrchat': return '#252542';
      default: return '#ffffff';
    }
  }

  function getSecondaryTextColor(theme: string): string {
    switch (theme) {
      case 'dark':
      case 'cyber':
      case 'vrchat':
        return 'rgba(255,255,255,0.6)';
      default:
        return 'rgba(0,0,0,0.5)';
    }
  }

  // Calculate card position within grid area
  function getCardStyle(card: Card): string {
    const x = card.x * (CELL_WIDTH + GAP);
    const y = card.y * (CELL_HEIGHT + GAP);
    const width = card.width * CELL_WIDTH + (card.width - 1) * GAP;
    const height = card.height * CELL_HEIGHT + (card.height - 1) * GAP;

    return `left: ${x}px; top: ${y}px; width: ${width}px; height: ${height}px;`;
  }

  function getSkeletonLines(card: Card): number {
    if (card.type === 'section') return 1;
    if (card.type === 'text') return Math.min(card.height * 2, 4);
    return 0;
  }
</script>

<!-- OGP render container - fixed 1200x630 -->
<div
  class="ogp-container"
  style="
    width: {OGP_WIDTH}px;
    height: {OGP_HEIGHT}px;
    background: {page ? getThemeBgColor(page.theme) : '#f8f9fa'};
    color: {page ? getThemeTextColor(page.theme) : '#1a1a1a'};
  "
>
  {#if loading}
    <div class="loading">Loading...</div>
  {:else if error}
    <div class="loading">Error</div>
  {:else if user && page}
    <!-- Left side: User info -->
    <div
      class="user-section"
      style="width: {SIDEBAR_WIDTH}px; padding: {PADDING}px;"
    >
      <!-- Large icon -->
      {#if user.iconUrl}
        <img
          src={user.iconUrl}
          alt=""
          class="user-icon"
        />
      {:else}
        <div class="user-icon-placeholder" />
      {/if}

      <!-- Display name -->
      <div class="user-name">
        {user.displayName || user.username}
      </div>

      <!-- Username -->
      <div
        class="user-handle"
        style="color: {getSecondaryTextColor(page.theme)};"
      >
        @{user.username}
      </div>

      <!-- Bio skeleton -->
      {#if user.bio}
        <div class="bio-skeleton">
          {#each Array(Math.min(3, Math.ceil(user.bio.length / 25))) as _, i}
            <div
              class="skeleton-line"
              style="width: {i === 2 ? '60%' : '90%'};"
            />
          {/each}
        </div>
      {/if}
    </div>

    <!-- Right side: Card grid -->
    <div
      class="cards-section"
      style="
        left: {GRID_LEFT_OFFSET}px;
        top: {PADDING}px;
        width: {ACTUAL_GRID_WIDTH}px;
        height: {GRID_AREA_HEIGHT}px;
      "
    >
      {#each gridCards as card (card.id)}
        <div
          class="card"
          style="
            {getCardStyle(card)}
            background: {getCardBgColor(page.theme)};
          "
        >
          {#if card.type === 'image' && card.data?.imageUrl}
            <img src={card.data.imageUrl} alt="" class="card-image" />
          {:else if card.type === 'link'}
            <div class="link-card">
              {#if card.ogp?.favicon}
                <img src={card.ogp.favicon} alt="" class="favicon" />
              {:else}
                <div class="favicon-placeholder" />
              {/if}
              <div class="skeleton-line" style="width: 50%;" />
            </div>
          {:else if card.type === 'section'}
            <div class="section-card">
              <div class="skeleton-line" style="width: 35%; height: 10px; opacity: 0.3;" />
              <div class="section-line" />
            </div>
          {:else if card.type === 'text'}
            <div class="text-card">
              {#each Array(getSkeletonLines(card)) as _, i}
                <div
                  class="skeleton-line"
                  style="width: {i % 3 === 2 ? '65%' : '90%'};"
                />
              {/each}
            </div>
          {:else if card.type === 'video'}
            <div class="video-card">
              <div class="play-button">
                <div class="play-icon" />
              </div>
            </div>
          {:else}
            <div class="empty-card" />
          {/if}
        </div>
      {/each}

      <!-- Bottom fade gradient -->
      <div
        class="fade-overlay"
        style="background: linear-gradient(to bottom, transparent 0%, {getThemeBgColor(page.theme)} 100%);"
      />
    </div>

    <!-- Logo -->
    <div
      class="logo"
      style="color: {getSecondaryTextColor(page.theme)};"
    >
      velf.me
    </div>
  {/if}
</div>

<style>
  .ogp-container {
    position: relative;
    font-family: 'Noto Sans JP', -apple-system, BlinkMacSystemFont, sans-serif;
    overflow: hidden;
  }

  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 24px;
    opacity: 0.5;
  }

  /* User section (left) */
  .user-section {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .user-icon {
    width: 140px;
    height: 140px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 20px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  }

  .user-icon-placeholder {
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: rgba(128,128,128,0.2);
    margin-bottom: 20px;
  }

  .user-name {
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 8px;
    text-align: center;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-handle {
    font-size: 16px;
    margin-bottom: 24px;
  }

  .bio-skeleton {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    padding: 0 20px;
  }

  /* Cards section (right) */
  .cards-section {
    position: absolute;
    overflow: hidden;
  }

  .fade-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 150px;
    pointer-events: none;
    z-index: 10;
  }

  .card {
    position: absolute;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }

  .card-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .link-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 8px;
    padding: 12px;
  }

  .favicon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }

  .favicon-placeholder {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: currentColor;
    opacity: 0.1;
  }

  .section-card {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 16px;
    gap: 12px;
  }

  .section-line {
    flex: 1;
    height: 2px;
    background: currentColor;
    opacity: 0.12;
  }

  .text-card {
    display: flex;
    flex-direction: column;
    padding: 14px;
    gap: 6px;
  }

  .video-card {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: currentColor;
    opacity: 0.05;
  }

  .play-button {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.3;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .play-icon {
    width: 0;
    height: 0;
    border-left: 14px solid currentColor;
    border-top: 9px solid transparent;
    border-bottom: 9px solid transparent;
    opacity: 0.6;
    margin-left: 3px;
  }

  .empty-card {
    width: 100%;
    height: 100%;
    background: currentColor;
    opacity: 0.03;
  }

  .skeleton-line {
    height: 8px;
    background: currentColor;
    opacity: 0.15;
    border-radius: 4px;
  }

  /* Logo */
  .logo {
    position: absolute;
    bottom: 20px;
    right: 28px;
    font-size: 16px;
    font-weight: 600;
    opacity: 0.5;
  }
</style>
